import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useSettings } from '../hooks/useSettings';

const Footer = () => {
  const { settings, loading } = useSettings();
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    
    setIsSubmitting(true);
    setSubscribeStatus('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSubscribeStatus(data.status === 'already_subscribed' ? 'already' : 'success');
        if (data.status !== 'already_subscribed') {
          setEmail('');
        }
      } else {
        setSubscribeStatus('error');
      }
    } catch (error) {
      console.error('Subscribe error:', error);
      setSubscribeStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };
  const socialMediaLinks = [
    { platform: 'Instagram', url: 'https://instagram.com/divecampadventures', icon: '📷', color: 'hover:text-pink-500' },
    { platform: 'Facebook', url: 'https://facebook.com/divecampadventures', icon: '📘', color: 'hover:text-blue-600' },
    { platform: 'WhatsApp', url: 'https://wa.me/61414710541', icon: '💬', color: 'hover:text-green-500' },
    { platform: 'YouTube', url: 'https://youtube.com/@divecampadventures', icon: '📺', color: 'hover:text-red-600' },
    { platform: 'TikTok', url: 'https://tiktok.com/@divecampadventures', icon: '🎵', color: 'hover:text-purple-600' }
  ];

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'Adventures', path: '/adventures' },
    { name: 'About Ahmed', path: '/about' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Reviews', path: '/reviews' },
    { name: 'Contact', path: '/contact' }
  ];

  const legalLinks = [
    { name: 'Privacy Policy', path: '/privacy' },
    { name: 'Terms of Service', path: '/terms' },
    { name: 'Admin', path: '/admin' }
  ];

  const adventureCategories = [
    { name: 'Beginner Diving', path: '/adventures?category=beginner' },
    { name: 'Certified Diving', path: '/adventures?category=certified' },
    { name: 'Camping Adventures', path: '/adventures?category=camping' },
    { name: 'Multi-day Trips', path: '/adventures?category=multiday' }
  ];

  return (
    <footer className="bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 text-white">
      {/* Newsletter Section */}
      <div className="border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <h3 className="text-3xl font-bold mb-4">Join the Dive Camp Tribe</h3>
            <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
              Get exclusive access to new adventures, special offers, and underwater photography tips. 
              Be the first to know about upcoming trips and community events.
            </p>
            
            {subscribeStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-500/20 text-green-300 rounded-xl border border-green-500/30 max-w-md mx-auto">
                <div className="flex items-center justify-center">
                  <span className="text-2xl mr-3">✅</span>
                  <div>
                    <div className="font-semibold">Successfully subscribed!</div>
                    <div className="text-sm opacity-90">Check your email for confirmation.</div>
                  </div>
                </div>
              </div>
            )}
            
            {subscribeStatus === 'already' && (
              <div className="mb-6 p-4 bg-blue-500/20 text-blue-300 rounded-xl border border-blue-500/30 max-w-md mx-auto">
                <div className="flex items-center justify-center">
                  <span className="text-2xl mr-3">ℹ️</span>
                  <div className="font-semibold">You are already subscribed!</div>
                </div>
              </div>
            )}
            
            {subscribeStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-500/20 text-red-300 rounded-xl border border-red-500/30 max-w-md mx-auto">
                <div className="flex items-center justify-center">
                  <span className="text-2xl mr-3">❌</span>
                  <div className="font-semibold">Something went wrong. Please try again.</div>
                </div>
              </div>
            )}
            
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row max-w-md mx-auto gap-4">
              <input
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="flex-1 px-4 py-3 rounded-xl bg-white/10 backdrop-blur border border-white/20 text-white placeholder-slate-300 focus:outline-none focus:ring-2 focus:ring-coral-500"
              />
              <button 
                type="submit"
                disabled={isSubmitting}
                className="bg-gradient-to-r from-coral-500 to-coral-600 hover:from-coral-600 hover:to-coral-700 disabled:from-slate-500 disabled:to-slate-600 px-8 py-3 rounded-xl font-semibold transition-all transform hover:scale-105 disabled:scale-100"
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand Section */}
          <div className="lg:col-span-1">
            <Link to="/" className="text-2xl font-bold text-white mb-4 block">
              🌊 Dive Camp Adventures
            </Link>
            <p className="text-slate-300 mb-6">
              Reconnect with nature through mindful diving and camping experiences. 
              Led by Ahmed Elsayed, MSDT certified instructor with 8+ years of experience.
            </p>
            <div className="flex space-x-4">
              {socialMediaLinks.map((social) => (
                <motion.a
                  key={social.platform}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`text-2xl transition-all transform hover:scale-110 ${social.color}`}
                  whileHover={{ y: -2 }}
                  title={social.platform}
                >
                  {social.icon}
                </motion.a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-slate-300 hover:text-coral-400 transition-colors duration-200"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Adventure Categories */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Adventures</h4>
            <ul className="space-y-3">
              {adventureCategories.map((category) => (
                <li key={category.name}>
                  <Link
                    to={category.path}
                    className="text-slate-300 hover:text-coral-400 transition-colors duration-200"
                  >
                    {category.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Get In Touch</h4>
            <div className="space-y-4 text-slate-300">
              <div className="flex items-center">
                <span className="text-coral-400 mr-3">📧</span>
                <a href={`mailto:${settings.contact_email}`} className="hover:text-coral-400 transition-colors">
                  {settings.contact_email}
                </a>
              </div>
              <div className="flex items-center">
                <span className="text-coral-400 mr-3">📱</span>
                <a href={`tel:${settings.contact_phone?.replace(/\s/g, '')}`} className="hover:text-coral-400 transition-colors">
                  {settings.contact_phone}
                </a>
              </div>
              <div className="flex items-center">
                <span className="text-coral-400 mr-3">💬</span>
                <a href={`https://wa.me/${settings.whatsapp_number?.replace(/[\s+]/g, '')}`} className="hover:text-coral-400 transition-colors">
                  WhatsApp: {settings.whatsapp_number}
                </a>
              </div>
              <div className="flex items-center">
                <span className="text-coral-400 mr-3">📍</span>
                <span>{settings.location}</span>
              </div>
              <div className="flex items-center">
                <span className="text-coral-400 mr-3">🕒</span>
                <span>{settings.business_hours}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-slate-400 text-sm mb-4 md:mb-0">
              © 2025 Dive Camp Adventures. All rights reserved. • ABN: {settings.abn}
            </div>
            <div className="flex space-x-6">
              {legalLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className="text-slate-400 hover:text-coral-400 text-sm transition-colors duration-200"
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-slate-800 text-center text-slate-500 text-xs">
            <p className="mb-2">🌊 Dive Deeper. Camp Wilder. Connect Mindfully. 🌊</p>
            <p>PADI MSDT Certified • Fully Insured • Eco-Conscious Adventures</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;